import React, { forwardRef, useState } from 'react';
import {
  Box,
  Button,
  Combobox,
  ComboboxEmptyMessage,
  ComboboxLoadingMessage,
  ComboboxOption,
  LabelledControl,
} from '@mos-cat/ds';
import { Magnifier as MagnifierIcon } from '@mos-cat/ds-icons';
import { pxToRem } from '@mos-cat/ds-theme';

import { getCurrentYear } from '../../helpers/date';
import { useDataContext } from '../../context/useDataContext';
import { Address, AddressRequestStatus } from '../../@types/address';
import { CheckDataStatus } from '../../@types/checkData';
import { STATS_EVENTS, sendStats } from '../../helpers/sendStats';
import { typografContent } from '../../helpers/typografContent';

const MIN_SEARCH_LENGTH = 3;

// typograf-enable
export default forwardRef<HTMLInputElement>(function SearchAddress(_, comboboxRef) {
  const {
    selectedAddress,
    addressListStatus,
    addressList,
    checkDataStatus,
    searchValue,
    setSearchValue,
    setCheckDataStatus,
    onSelectValidAddress,
    resetSelectedAddress,
    debouncedGetAddress,
    abortGetAddress,
  } = useDataContext();
  const [isFocusOnSelect, setIsFocusOnSelect] = useState(false);

  const currentYear = getCurrentYear();
  const trimmedSearchValue = searchValue.trim();
  const isLoadingMessageVisible = addressListStatus === AddressRequestStatus.Fetching;
  const hasAddressListResults =
    addressListStatus === AddressRequestStatus.Success && addressList.length > 0;
  const isEmptyMessageVisible =
    addressListStatus === AddressRequestStatus.Success && addressList.length === 0;
  const error =
    checkDataStatus === CheckDataStatus.ValidateError
      ? 'Введите и выберите из списка адрес с номером дома'
      : undefined;

  const handleChange = (value: string) => {
    setCheckDataStatus(CheckDataStatus.Initial);
    setSearchValue(value);
    resetSelectedAddress();
    const trimmedValue = value.trim();
    const isValueChanged = trimmedSearchValue !== trimmedValue;
    const isValidSearchLength = trimmedValue.length >= MIN_SEARCH_LENGTH;
    if (isValueChanged && isValidSearchLength) {
      debouncedGetAddress({ query: value });
    } else if (!isValidSearchLength) {
      abortGetAddress();
    }
  };

  const handleSelect = (addressValue: string) => {
    setIsFocusOnSelect(true);
    setCheckDataStatus(CheckDataStatus.Initial);
    const address = JSON.parse(addressValue) as Address;
    const { Address, body = '' } = address;
    setSearchValue(Address ?? body);
    const isAddressWithUnom = 'UNOM' in address;
    if (!isAddressWithUnom) {
      debouncedGetAddress({ query: body, houseOnly: true });
    } else {
      onSelectValidAddress(address);
    }
  };

  const handleValidateError = () => {
    setCheckDataStatus(CheckDataStatus.ValidateError);
    if (comboboxRef != null && typeof comboboxRef !== 'function') {
      comboboxRef.current?.focus();
    }
  };

  const handleSearchClick = () => {
    const isShortQuery = trimmedSearchValue.length < MIN_SEARCH_LENGTH;
    if (isShortQuery) {
      handleValidateError();
    } else if (addressListStatus === AddressRequestStatus.Error) {
      setCheckDataStatus(CheckDataStatus.TechError);
    } else if (!selectedAddress) {
      handleValidateError();
    } else {
      sendStats(STATS_EVENTS.ADDRESS, searchValue);
      setCheckDataStatus(CheckDataStatus.Success);
    }
  };

  const handleFocus = () => {
    if (!isFocusOnSelect) {
      sendStats(STATS_EVENTS.FOCUS);
    }
    setIsFocusOnSelect(false);
  };

  const label = typografContent(
    `Введите свой адрес и узнайте, когда отключат воду в ${currentYear} году`,
  );
  const typografedError = error ? typografContent(error) : '';

  return (
    <Box display="flex" alignItems="flex-end">
      <LabelledControl
        as={Combobox}
        aria-label={`${label}, ${typografedError}`}
        label={label}
        error={typografedError}
        ref={comboboxRef}
        hasError={!!error}
        name="CustomCombobox"
        popoverAriaLabel="Список адресов"
        value={searchValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onSelect={handleSelect}
        maxWidth={pxToRem(724)}
      >
        {isLoadingMessageVisible && <ComboboxLoadingMessage />}
        {hasAddressListResults &&
          addressList!.map((value) => {
            return (
              <ComboboxOption key={value.Address} value={JSON.stringify(value)}>
                {value.Address}
              </ComboboxOption>
            );
          })}
        {isEmptyMessageVisible && <ComboboxEmptyMessage />}
      </LabelledControl>
      <Box p="sp-4" />
      <Button
        kind="primary"
        styles={{ width: [pxToRem(48), 'auto'], flexShrink: 0 }}
        onClick={handleSearchClick}
      >
        <Box as={MagnifierIcon} aria-label="Найти" display={['block', 'none']} />
        <Box display={['none', 'block']}>Найти</Box>
      </Button>
    </Box>
  );
});
